// SessionViewer.js — Dark Pattern Review Tool
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactFlow, { Background, Controls, useNodesState, useEdgesState } from "reactflow";
import "reactflow/dist/style.css";
import {
    fetchSession,
    updateDarkPattern,
    addDarkPattern,
    saveImageFeedback,
    confirmDarkPattern,
    updateLaw,
    addLaw as addLawAPI,
    exportImageProperties,
    fetchJourneyNavigation,
    revokeDarkPattern,
    fetchImageProcessingData,
    fetchAgentTraces,
    getOrgId,
    generateSessionSummary,
    exportSessionSummary,
    fetchLawMaster
} from "../api";

import { toast } from "react-toastify";
import ClientHeader from "./ClientHeader";
const PRIORITY_COLORS = {
    'High Priority': { bg: '#FF6B6B', text: '#fff' },
    'High': { bg: '#FFA726', text: '#fff' },
    'Medium': { bg: '#FFD93D', text: '#000' },
    'Low': { bg: '#66BB6A', text: '#fff' },
    'Lowest': { bg: '#E0E0E0', text: '#000' },
};

const PRIORITY_LABELS = {
    1: 'High Priority',
    2: 'High',
    3: 'Medium',
    4: 'Low',
    5: 'Lowest'
};

const normalizePriority = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    const priorityMap = {
        '1': '1',
        'high priority': '1',
        'high-priority': '1',
        '2': '2',
        'high': '2',
        '3': '3',
        'medium': '3',
        '4': '4',
        'low': '4',
        '5': '5',
        'lowest': '5'
    };

    return priorityMap[normalized] || '3';
};

const toApiPriority = (value) => {
    const normalized = normalizePriority(value);
    const apiPriorityMap = {
        '1': 'high priority',
        '2': 'high',
        '3': 'medium',
        '4': 'low',
        '5': 'lowest'
    };

    return apiPriorityMap[normalized] || 'medium';
};

const normalizeCorrectnessType = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    return ['correct', 'incorrect', 'redundant', 'duplicate'].includes(normalized) ? normalized : '';
};

const JURISDICTION_PRIORITY = {
    'India': 1,
    'USA': 2,
    'EU': 3,
    'UK': 4,
    'China': 5,
    'N/A': 999
};

const clauseNumbers = (text) => {
    const matches = String(text || '').match(/\d+/g);
    return matches ? matches.map((n) => parseInt(n, 10)) : [];
};

const compareClauses = (a, b) => {
    const numsA = clauseNumbers(a?.clause);
    const numsB = clauseNumbers(b?.clause);
    const len = Math.max(numsA.length, numsB.length);
    for (let i = 0; i < len; i += 1) {
        const na = numsA[i];
        const nb = numsB[i];
        if (na == null) return -1;
        if (nb == null) return 1;
        if (na !== nb) return na - nb;
    }
    return String(a?.clause || '').localeCompare(String(b?.clause || ''), undefined, {
        numeric: true,
        sensitivity: 'base'
    });
};


// ── ReactFlow node builder (Vertical Tree Flow - Matching NewEditor.js) ────────────────
const buildFlowGraph = (session, journeys, onImageClick) => {
    let nodeIdCounter = 0;
    const getNodeId = () => `node-${nodeIdCounter++}`;
    const getSafeLabel = (value, fallback) => {
        if (typeof value === "string" && value.trim()) return value.trim();
        return fallback;
    };

    const nodes = [];
    const edges = [];

    // Spacing constants
    const minJourneySpacing = 250;
    const maxJourneySpacing = 350;
    const availableWidth = 1200;

    let journeySpacing = minJourneySpacing;
    if (journeys.length > 0) {
        const totalDesiredWidth = journeys.length * maxJourneySpacing;
        if (totalDesiredWidth <= availableWidth) {
            journeySpacing = maxJourneySpacing;
        } else {
            journeySpacing = Math.max(minJourneySpacing, availableWidth / journeys.length);
        }
    }

    const totalWidth = journeys.length * journeySpacing;
    const startX = Math.max(50, (availableWidth - totalWidth) / 2);

    // Root domain node (centered at top)
    const rootId = getNodeId();
    const domainX = startX + (totalWidth / 2) - 100;
    nodes.push({
        id: rootId,
        position: { x: domainX, y: 80 },
        data: { label: session.domain_name || "Domain", nodeType: 'domain' },
        style: {
            background: '#E0E7FF',
            color: '#3730A3',
            border: '2px solid #C7D2FE',
            borderRadius: '12px',
            padding: '12px 20px',
            fontSize: '16px',
            fontWeight: 'bold',
            minWidth: '200px',
            textAlign: 'center'
        },
        sourcePosition: 'bottom',
        targetPosition: 'top'
    });

    const journeyY = 250;
    const imageStartY = 400;
    const imageSpacing = 120;

    journeys.forEach((journey, journeyIndex) => {
        const journeyId = getNodeId();
        const journeyX = startX + (journeyIndex * journeySpacing);
        const journeyLabel = getSafeLabel(
            // journey?.user_journey_name || journey?.journey_title || journey?.name,
            journey?.user_journey_name,
            `Journey ${journeyIndex + 1}`
        );

        nodes.push({
            id: journeyId,
            position: { x: journeyX, y: journeyY },
            data: { label: journeyLabel, nodeType: 'journey' },
            style: {
                background: '#D1FAE5',
                color: '#065F46',
                border: '2px solid #A7F3D0',
                borderRadius: '10px',
                padding: '10px 16px',
                fontSize: '14px',
                fontWeight: '600',
                minWidth: '180px',
                textAlign: 'center'
            },
            sourcePosition: 'bottom',
            targetPosition: 'top'
        });

        edges.push({
            id: `edge-${rootId}-${journeyId}`,
            source: rootId,
            target: journeyId,
            type: 'smoothstep',
            animated: true,
            style: { strokeWidth: 2, stroke: '#6B7280' }
        });

        const images = journey.images || [];
        let previousImageId = null;

        images.forEach((image, imageIndex) => {
            const imageId = getNodeId();
            const imageX = journeyX;
            const imageY = imageStartY + (imageIndex * imageSpacing);
            // const hasIssues = Array.isArray(image.dark_patterns) && image.dark_patterns.length > 0;
            const pageLabel = getSafeLabel(
                // image?.page_name || image?.page_title || image?.name,
                image?.page_name,
                `Screen ${imageIndex + 1}`
            );

            nodes.push({
                id: imageId,
                position: { x: imageX, y: imageY },
                data: {
                    label: pageLabel,
                    nodeType: 'image',
                    onClick: () => onImageClick(journeyIndex, imageIndex)
                },
                style: {
                    // background: hasIssues ? '#fff5f0' : '#FEE2E2',
                    background: '#FEE2E2',
                    color: '#991B1B',
                    // border: hasIssues ? '2.5px solid #fb923c' : '2px solid #FECACA',
                    border: '2px solid #FECACA',
                    borderRadius: '8px',
                    padding: '8px 14px',
                    fontSize: '12px',
                    fontWeight: '500',
                    minWidth: '160px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                },
                sourcePosition: 'bottom',
                targetPosition: 'top'
            });

            if (imageIndex === 0) {
                edges.push({
                    id: `edge-${journeyId}-${imageId}`,
                    source: journeyId,
                    target: imageId,
                    type: 'smoothstep',
                    animated: true,
                    style: { strokeWidth: 2, stroke: '#6B7280' }
                });
            } else if (previousImageId) {
                edges.push({
                    id: `edge-${previousImageId}-${imageId}`,
                    source: previousImageId,
                    target: imageId,
                    type: 'smoothstep',
                    animated: true,
                    style: { strokeWidth: 2, stroke: '#6B7280' }
                });
            }
            previousImageId = imageId;
        });
    });

    return { nodes, edges };
};

const SessionViewer = ({ user, onLogout }) => {
    const { sessionId } = useParams();
    const navigate = useNavigate();

    // ── BASIC STATE ─────────────────────────────────────────────────────────────
    const [session, setSession] = useState(null);
    const [journeys, setJourneys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // ── NAVIGATION & TABS ───────────────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState("all-screens"); // "all-screens", "detailed-review"
    const [currentJourneyIndex, setCurrentJourneyIndex] = useState(0);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // ── IMAGE VIEW STATE ────────────────────────────────────────────────────────
    const [zoomLevel, setZoomLevel] = useState(1);
    const [imagePos, setImagePos] = useState({ x: 0, y: 0 });
    const isDragging = useRef(false);
    const dragStart = useRef({ x: 0, y: 0 });


    // ── SPLIT VIEW STATE ────────────────────────────────────────────────────────
    const [rightPanelWidth, setRightPanelWidth] = useState(450);
    const [isResizing, setIsResizing] = useState(false);
    const containerRef = useRef(null);

    // ── FINDINGS & POINTS ───────────────────────────────────────────────────────
    const [points, setPoints] = useState([]);
    const [internalFeedback, setInternalFeedback] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [actionMsg, setActionMsg] = useState("");
    const [editingDPId, setEditingDPId] = useState(null);
    const [editingDraft, setEditingDraft] = useState(null);

    // ── POPUP STATES ────────────────────────────────────────────────────────────
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalConfig, setModalConfig] = useState({ type: null, data: null });
    const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
    const [lawsModalLaws, setLawsModalLaws] = useState([]);
    const [lawsFilterJurisdiction, setLawsFilterJurisdiction] = useState("");

    // ── REACTFLOW STATE ─────────────────────────────────────────────────────────
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);

    // ── AIRTABLE & EXPORT STATES ──────────────────────────────────────────────────
    const [isExportPDFLoading, setIsExportPDFLoading] = useState(false);
    const [isDarkPatternsLoading, setIsDarkPatternsLoading] = useState(false);
    const [darkPatterns, setDarkPatterns] = useState([]);
    const [lawsList, setLawsList] = useState([]);
    const [clausesList, setClausesList] = useState([]);
    const [lawClauseRows, setLawClauseRows] = useState([]);
    const [isAddingLaw, setIsAddingLaw] = useState(false);
    const [selectedLawId, setSelectedLawId] = useState("");
    const [selectedClause, setSelectedClause] = useState("");
    const [selectedLink, setSelectedLink] = useState("");
    const [selectedJurisdiction, setSelectedJurisdiction] = useState("");
    const [selectedGalleryImageIndex, setSelectedGalleryImageIndex] = useState(0);
    const [journeyNavigation, setJourneyNavigation] = useState([]);
    const [isNavigationLoading, setIsNavigationLoading] = useState(false);
    const [processingData, setProcessingData] = useState(null);
    const [isProcessingDataLoading, setIsProcessingDataLoading] = useState(false);
    const [agentTraces, setAgentTraces] = useState([]);

    // ── SUMMARY MODAL STATE ────────────────────────────────────────────────────
    const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
    const [summaryText, setSummaryText] = useState("");
    const [isSummaryGenerating, setIsSummaryGenerating] = useState(false);
    const [isSummaryExporting, setIsSummaryExporting] = useState(false);
    const [summaryError, setSummaryError] = useState("");

    // ── HELPERS ────────────────────────────────────────────────────────────────
    const currentJourney = journeys[currentJourneyIndex] || null;
    const currentImage = currentJourney?.images?.[currentImageIndex] || null;
    const totalImagesCount = journeys.reduce((total, journey) => total + (journey?.images?.length || 0), 0);
    const imagesBeforeCurrentJourney = journeys
        .slice(0, currentJourneyIndex)
        .reduce((total, journey) => total + (journey?.images?.length || 0), 0);
    const globalImageIndex = totalImagesCount > 0 ? imagesBeforeCurrentJourney + currentImageIndex + 1 : 0;

    const showMsg = (msg, isError = false) => {
        setActionMsg(isError ? `⚠ ${msg}` : `✓ ${msg}`);
        setTimeout(() => setActionMsg(""), 3000);
    };

    // ── DATA LOADING ───────────────────────────────────────────────────────────
    const loadData = useCallback(async () => {
        console.log("Loading data for sessionId:", sessionId);
        if (!sessionId) return;
        setLoading(true);
        try {
            const data = await fetchSession(sessionId);
            console.log("API Response:", data);
            if (data?.session) {
                setSession(data.session);
                const journeyList = data.user_journeys || [];
                setJourneys(journeyList);
                // Sync current findings
                const currentImg = journeyList?.[currentJourneyIndex]?.images?.[currentImageIndex];
                if (currentImg) {
                    const normalizedPoints = (currentImg.dark_patterns || []).map((p) => ({
                        ...p,
                        priority: normalizePriority(p.priority)
                    }));
                    setPoints(normalizedPoints);
                    setInternalFeedback(currentImg.img_feedback || "");
                }
            } else {
                setError("Session not found");
            }
        } catch (err) {
            setError(err.message || "Failed to load session");
        } finally {
            setLoading(false);
        }
    }, [sessionId, currentJourneyIndex, currentImageIndex]);

    useEffect(() => { loadData(); }, [loadData]);

    useEffect(() => {
        setSelectedGalleryImageIndex(0);
    }, [currentJourneyIndex, currentImageIndex]);

    const fetchDarkPatterns = async () => {
        setIsDarkPatternsLoading(true);
        try {
            const Airtable = require('airtable');
            const base = new Airtable({ apiKey: process.env.REACT_APP_AIRTABLE_API_KEY }).base(process.env.REACT_APP_AIRTABLE_BASE_ID || 'apprwDgpLMtBvjfTQ');
            const records = await base('tblXajPljs2LJPtfe').select().all();
            const patterns = records.map(record => {
                const nameValue = record.fields['fldwHbFBq8qyStXvp'] || record.fields['Name'] || record.fields.Name || 'Unknown Pattern';
                return { id: record.id, name: nameValue };
            }).filter(pattern => {
                if (pattern.name === 'Unknown Pattern') return false;
                return !pattern.name.toLowerCase().includes('archive');
            });
            patterns.sort((a, b) => a.name.localeCompare(b.name));
            setDarkPatterns(patterns);
        } catch (error) {
            console.error('Error fetching dark patterns:', error);
        } finally {
            setIsDarkPatternsLoading(false);
        }
    };

    const fetchLawsWithRanking = async () => {
        setLoading(true);
        try {
            const rows = await fetchLawMaster();
            const usableRows = rows
                .map((row) => ({
                    id: row.id,
                    lawName: String(row.lawName || "").replace(/\s+/g, " ").trim(),
                    country: String(row.country || "N/A").replace(/\s+/g, " ").trim(),
                    clauseArticle: String(row.clauseArticle || "").replace(/\s+/g, " ").trim(),
                    type: row.type || '',
                    reviewStatus: String(row.reviewStatus || "").trim()
                }))
                .filter(row => row.lawName && row.reviewStatus !== 'Human Review');

            setLawClauseRows(usableRows);

            const uniqueLawsMap = new Map();
            usableRows.forEach((row) => {
                const uniqueKey = `${row.lawName}__${row.country}`;
                if (!uniqueLawsMap.has(uniqueKey)) {
                    uniqueLawsMap.set(uniqueKey, {
                        id: uniqueKey,
                        shortName: row.lawName,
                        jurisdiction: row.country
                    });
                }
            });

            const laws = Array.from(uniqueLawsMap.values());
            laws.sort((a, b) => {
                const priorityDiff = (JURISDICTION_PRIORITY[a.jurisdiction] || 999) - (JURISDICTION_PRIORITY[b.jurisdiction] || 999);
                if (priorityDiff !== 0) return priorityDiff;
                return (a.shortName || '').localeCompare(b.shortName || '');
            });
            setLawsList(laws);
        } catch (error) {
            console.error('Error fetching laws:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchClausesForLaw = async (lawId) => {
        setLoading(true);
        try {
            const selectedLaw = lawsList.find(law => law.id === lawId);
            const selectedCountry = selectedLaw?.jurisdiction || 'N/A';
            const selectedLawName = selectedLaw?.shortName || '';

            setSelectedJurisdiction(selectedCountry);

            const matchingRows = lawClauseRows.filter(
                (row) =>
                    row.lawName === selectedLawName &&
                    (row.country || 'N/A') === selectedCountry &&
                    row.reviewStatus !== 'Human Review'
            );

            const uniqueClauses = new Map();
            matchingRows.forEach((row) => {
                const clauseText = row.clauseArticle || '';
                if (!clauseText) return;
                if (!uniqueClauses.has(clauseText)) {
                    uniqueClauses.set(clauseText, {
                        id: `${lawId}__${clauseText}`,
                        clause: clauseText,
                        link: ''
                    });
                }
            });

            setClausesList(Array.from(uniqueClauses.values()).sort(compareClauses));
        } catch (error) {
            console.error('Error fetching clauses:', error);
        } finally {
            setLoading(false);
        }
    };

    // ── SUMMARY HANDLERS ──────────────────────────────────────────────────────
    const handleOpenSummaryModal = async () => {
        setIsSummaryModalOpen(true);
        setIsSummaryGenerating(true);
        setSummaryText("");
        setSummaryError("");

        try {
            await generateSessionSummary(sessionId, {
                onToken: (text) => setSummaryText(prev => prev + text),
                onDone: () => setIsSummaryGenerating(false),
                onError: (msg) => {
                    setSummaryError(msg || "Failed to generate summary");
                    setIsSummaryGenerating(false);
                },
            });
        } catch (err) {
            setSummaryError(err.message || "Failed to generate summary");
            setIsSummaryGenerating(false);
        }
    };

    const handleExportSummaryPDF = async () => {
        if (!summaryText.trim()) return;
        setIsSummaryExporting(true);
        try {
            const blob = await exportSessionSummary(sessionId, {
                summary_text: summaryText,
                session_name: session?.session_name || sessionId,
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `AuditSummary_${(session?.session_name || sessionId).replace(/\s+/g, "_")}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success("Summary PDF downloaded!");
        } catch (err) {
            toast.error(err.message || "Failed to export PDF");
        } finally {
            setIsSummaryExporting(false);
        }
    };

    const handleExportProperties = async () => {
        if (!currentImage) return;
        const selectedNode = currentImage;
        const capturedAt = selectedNode.created_at;
        let formattedDate = "None";
        if (capturedAt) {
            try {
                if (typeof capturedAt === "string") formattedDate = new Date(capturedAt).toLocaleString();
                else if (capturedAt?.seconds) formattedDate = new Date(capturedAt.seconds * 1000).toLocaleString();
                else formattedDate = new Date(capturedAt).toLocaleString();
            } catch (_) { formattedDate = "None"; }
        }
        const imgUrl = selectedNode.webpage_url || selectedNode.image_url || "";
        const imageHash = selectedNode.image_hash || (imgUrl ? "H-" + Math.abs(imgUrl.split("").reduce((a, b) => { a = ((a << 5) - a) + b.charCodeAt(0); return a | 0; }, 0)).toString(16) : "None");
        const journeyName = currentJourney?.user_journey_name || 'None';
        const createdBy = session?.created_by || 'None';
        const pageName = selectedNode.page_name || 'None';
        const websiteCategory = session?.domain_name || 'None';

        const selectedJourneyNavigation = currentJourney?.navigation_metadata?.navigationData || [];
        let visibleNavigation = [];
        if (selectedJourneyNavigation && selectedJourneyNavigation.length > 0) {
            let captureIndex = selectedJourneyNavigation.findIndex(nav => (nav.image_id === selectedNode.image_id) || (nav.url === imgUrl));
            const idx = captureIndex === -1 ? 0 : captureIndex;
            const startIndex = Math.max(0, idx - 2);
            visibleNavigation = selectedJourneyNavigation.slice(startIndex, idx + 4);
        }

        const exportPayload = {
            title: "Dark pattern analysis", journeyName, createdBy,
            url: imgUrl || 'None', pageName, websiteCategory,
            capturedAt: formattedDate, imageHash,
            encryptionType: "Google-managed (AES-256 encryption)",
            imageUrl: imgUrl, visibleNavigation
        };

        try {
            setIsExportPDFLoading(true);
            toast.info("Generating PDF, please wait...");
            const blob = await exportImageProperties(selectedNode.image_id, exportPayload);
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', `Dark_Pattern_Analysis_${selectedNode.image_id || 'export'}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
            toast.success("PDF exported successfully!");
        } catch (error) {
            console.error("Error exporting PDF:", error);
            toast.error("An error occurred while exporting the PDF.");
        } finally {
            setIsExportPDFLoading(false);
        }

    };

    useEffect(() => {
        fetchDarkPatterns();
        fetchLawsWithRanking();
    }, []);


    useEffect(() => {
        if (isModalOpen && modalConfig.type === "laws" && modalConfig.data) {
            const point = modalConfig.data;
            const raw = point?.laws || point?.related_laws || [];
            const normalized = raw.map((l, i) => ({
                id: `law-${i}-${l.law_id || l.law_name || i}`,
                realId: l.law_id,
                law: l.law_name || l.law || l.name || "Unknown Law",
                jurisdiction: l.jurisdiction || l.region || "EU",
                clause: l.clause_name || l.clause || l.article || "—",
                link: l.link || l.url || l.link_to_passage || "",
                correctness_type: normalizeCorrectnessType(l.correctness_type)
            }));
            setLawsModalLaws(normalized);
            setLawsFilterJurisdiction("");
        }
    }, [isModalOpen, modalConfig.type, modalConfig.data]);

    // ── REACTFLOW UPDATE ───────────────────────────────────────────────────────
    useEffect(() => {
        if (!session || journeys.length === 0) return;
        const { nodes: newNodes, edges: newEdges } = buildFlowGraph(session, journeys, (ji, ii) => {
            setCurrentJourneyIndex(ji);
            setCurrentImageIndex(ii);
            setActiveTab("detailed-review");
            setZoomLevel(1);
            setImagePos({ x: 0, y: 0 });
        });
        setNodes(newNodes);
        setEdges(newEdges);
    }, [session, journeys, setNodes, setEdges]);

    // ── HANDLERS ───────────────────────────────────────────────────────────────
    const handleNodeClick = useCallback((event, node) => {
        if (node.data?.onClick) node.data.onClick();
    }, []);

    const handlePrev = () => {
        if (currentImageIndex > 0) {
            setCurrentImageIndex(currentImageIndex - 1);
        } else if (currentJourneyIndex > 0) {
            const prevJI = currentJourneyIndex - 1;
            setCurrentJourneyIndex(prevJI);
            setCurrentImageIndex(journeys[prevJI].images.length - 1);
        }
        setZoomLevel(1); setImagePos({ x: 0, y: 0 });
    };

    const handleNext = () => {
        if (currentImageIndex < (currentJourney?.images?.length || 0) - 1) {
            setCurrentImageIndex(currentImageIndex + 1);
        } else if (currentJourneyIndex < journeys.length - 1) {
            setCurrentJourneyIndex(currentJourneyIndex + 1);
            setCurrentImageIndex(0);
        }
        setZoomLevel(1); setImagePos({ x: 0, y: 0 });
    };

    const handleConfirmDP = async (dpId) => {
        setIsProcessing(true);
        try {
            await confirmDarkPattern(dpId);
            showMsg("Dark pattern confirmed.");
            loadData();
        } catch (err) {
            showMsg(err.message, true);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleRevokeDP = async (dpId) => {
        setIsProcessing(true);
        try {
            await revokeDarkPattern(dpId);
            showMsg("Dark pattern revoked.");
            loadData();
        } catch (err) {
            showMsg(err.message, true);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleStartEdit = (p) => {
        setEditingDPId(p.dark_pattern_id);
        setEditingDraft({
            response_type: p.response_type || "outcome",
            priority: normalizePriority(p.priority),
            dark_pattern_type: p.dark_pattern_type || "",
            dark_pattern_description: p.dark_pattern_description || ""
        });
    };

    const handleCancelEdit = () => {
        if (editingDPId && String(editingDPId).startsWith('new-')) {
            setPoints(prev => prev.filter(p => p.dark_pattern_id !== editingDPId));
        }
        setEditingDPId(null);
        setEditingDraft(null);
    };

    const handleAddNewPoint = () => {
        if (!currentImage?.image_id) {
            showMsg("No image selected.", true);
            return;
        }
        const newId = `new-${Date.now()}`;
        const newPoint = {
            dark_pattern_id: newId,
            response_type: "outcome",
            priority: "3",
            dark_pattern_type: "",
            dark_pattern_description: ""
        };
        setPoints(prev => [newPoint, ...prev]);
        setEditingDPId(newId);
        setEditingDraft({ ...newPoint });
    };

    const handleSaveEdit = async () => {
        if (!editingDPId || !editingDraft) return;
        setIsProcessing(true);
        try {
            const isNew = String(editingDPId).startsWith('new-');
            if (isNew) {
                const payload = {
                    image_id: currentImage?.image_id,
                    response_type: editingDraft.response_type || "outcome",
                    priority: toApiPriority(editingDraft.priority),
                    dark_pattern_type: editingDraft.dark_pattern_type || "",
                    dark_pattern_description: editingDraft.dark_pattern_description || ""
                };
                await addDarkPattern(payload);
                showMsg("New finding added.");
            } else {
                await handleUpdatePoint(editingDPId, {
                    ...editingDraft,
                    priority: toApiPriority(editingDraft.priority)
                });
            }
            handleCancelEdit();
            loadData();
        } catch (err) {
            showMsg(err.message, true);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleUpdateLawStatus = async (lawId, correctness) => {
        if (!lawId) {
            toast.error("Error: Law ID missing. Cannot update.");
            return;
        }
        const normalizedStatus = normalizeCorrectnessType(correctness);
        if (!normalizedStatus) return;
        try {
            await updateLaw(lawId, { correctness_type: normalizedStatus });
            showMsg(`Law marked as ${normalizedStatus}.`);
            setLawsModalLaws(prev => prev.map(l => (l.realId === lawId || l.id === lawId) ? { ...l, correctness_type: normalizedStatus } : l));
            loadData();
        } catch (err) {
            showMsg(err.message, true);
        }
    };

    // ── MODAL HELPERS ──────────────────────────────────────────────────────────
    const openPopup = async (type, data = null) => {
        setModalConfig({ type, data });
        setIsModalOpen(true);

        if (type === 'properties' && currentJourney?.user_journey_id) {
            setIsNavigationLoading(true);
            try {
                const navRes = await fetchJourneyNavigation(currentJourney.user_journey_id);
                setJourneyNavigation(navRes.navigationData || []);
            } catch (err) {
                console.error("Failed to fetch journey navigation:", err);
                toast.error("Failed to load journey navigation data.");
            } finally {
                setIsNavigationLoading(false);
            }
        } else if (type === 'detailed-view' && currentImage?.image_id) {
            setIsProcessingDataLoading(true);
            try {
                const pData = await fetchImageProcessingData(currentImage.image_id);
                setProcessingData(pData);
                const tracesRes = await fetchAgentTraces(currentImage.image_id);
                setAgentTraces(tracesRes || []);
            } catch (err) {
                console.error("Failed to fetch data:", err);
                toast.error("Failed to load processing data or agent traces.");
            } finally {
                setIsProcessingDataLoading(false);
            }
        }
    };

    const closePopup = () => {
        setIsModalOpen(false);
        setModalConfig({ type: null, data: null });
    };

    // ── IMAGE VIEWER CONTROLS ──────────────────────────────────────────────────
    const handleWheel = (e) => {
        if (e.ctrlKey) {
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.1 : 0.1;
            setZoomLevel(prev => Math.min(Math.max(prev + delta, 0.5), 5));
        }
    };

    const handleMouseDown = (e) => {
        isDragging.current = true;
        dragStart.current = { x: e.clientX - imagePos.x, y: e.clientY - imagePos.y };
    };

    const handleMouseMove = (e) => {
        if (!isDragging.current) return;
        setImagePos({ x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y });
    };

    const handleMouseUp = () => { isDragging.current = false; };

    // ── RESIZING ────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!isResizing) return;
        const onMove = (e) => {
            if (!containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            const width = rect.right - e.clientX;
            setRightPanelWidth(Math.min(Math.max(width, 300), 800));
        };
        const onUp = () => setIsResizing(false);
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    }, [isResizing]);

    // ── ACTIONS ─────────────────────────────────────────────────────────────────
    const handleSaveAll = async () => {
        if (!currentImage) return;
        setIsProcessing(true);
        try {
            await saveImageFeedback(currentImage.image_id, internalFeedback);
            showMsg("Session review updated.");
            loadData();
        } catch (err) {
            showMsg(err.message, true);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleUpdatePoint = async (dpId, payload) => {
        try {
            await updateDarkPattern(dpId, payload);
            showMsg("Finding updated.");
            loadData();
        } catch (err) {
            showMsg(err.message, true);
        }
    };

    // ── RENDERERS ──────────────────────────────────────────────────────────────

    const renderPopups = () => {
        if (!isModalOpen) return null;
        const { type, data } = modalConfig;

        if (type === 'properties') {
            const selectedNode = currentImage;
            const selectedJourneyNavigation = journeyNavigation;
            const displayUrl = selectedNode?.webpage_url || selectedNode?.image_url || "";
            const raw = selectedNode?.created_at;
            let capturedAt = "None";
            if (raw) {
                try {
                    if (typeof raw === "string") capturedAt = new Date(raw).toLocaleString();
                    else if (raw?.seconds) capturedAt = new Date(raw.seconds * 1000).toLocaleString();
                    else if (typeof raw === "number") capturedAt = new Date(raw > 1e12 ? raw : raw * 1000).toLocaleString();
                    else capturedAt = new Date(raw).toLocaleString();
                } catch (_) { capturedAt = "None"; }
            }
            const imageHash = selectedNode?.image_hash || (displayUrl ? "H-" + Math.abs(displayUrl.split("").reduce((a, b) => { a = ((a << 5) - a) + b.charCodeAt(0); return a | 0; }, 0)).toString(16) : "None");

            return (
                <div className="modal-overlay" onClick={closePopup} style={{ backgroundColor: "rgba(0,0,0,0.7)" }}>
                    <div className="image-properties-modal" onClick={e => e.stopPropagation()} style={{
                        backgroundColor: '#fff', borderRadius: '16px', width: '95%', height: '90%',
                        maxWidth: '1440px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                        overflow: 'hidden', display: 'flex', flexDirection: 'column'
                    }}>
                        <div className="img-props-header" style={{ padding: '20px 24px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                            <div>
                                <h3 className="img-props-title" style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#1a1a1a' }}>Image Properties</h3>
                                <p className="img-props-url" style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#666' }}>{displayUrl}</p>
                            </div>
                            <button className="img-props-close" onClick={closePopup} style={{ background: '#f5f5f5', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666', width: '36px', height: '36px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&times;</button>
                        </div>

                        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                            {/* Left Side: Properties */}
                            <div style={{
                                width: '40%',
                                minWidth: '350px',
                                borderRight: '1px solid #f0f0f0',
                                padding: '24px',
                                overflowY: 'auto',
                                backgroundColor: '#fafafa'
                            }}>
                                <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a1a', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ width: '4px', height: '16px', backgroundColor: '#000EAB', borderRadius: '2px' }}></span>
                                    Properties
                                </h4>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #f0f0f0' }}>
                                        <label style={{ display: 'block', fontWeight: '600', color: '#888', fontSize: '11px', textTransform: 'uppercase', marginBottom: '4px' }}>Journey Name</label>
                                        <div style={{ fontSize: '14px', color: '#000EAB', fontWeight: '600' }}>
                                            {currentJourney?.user_journey_name || "None"}
                                        </div>
                                    </div>

                                    <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #f0f0f0' }}>
                                        <label style={{ display: 'block', fontWeight: '600', color: '#888', fontSize: '11px', textTransform: 'uppercase', marginBottom: '4px' }}>Captured By</label>
                                        <div style={{ fontSize: '14px', color: '#333', fontWeight: '500' }}>
                                            {session?.created_by || 'None'}
                                        </div>
                                    </div>

                                    <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #f0f0f0' }}>
                                        <label style={{ display: 'block', fontWeight: '600', color: '#888', fontSize: '11px', textTransform: 'uppercase', marginBottom: '4px' }}>Page Name</label>
                                        <div style={{ fontSize: '14px', color: '#333', fontWeight: '500' }}>
                                            {selectedNode?.page_name || "None"}
                                        </div>
                                    </div>

                                    <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #f0f0f0' }}>
                                        <label style={{ display: 'block', fontWeight: '600', color: '#888', fontSize: '11px', textTransform: 'uppercase', marginBottom: '4px' }}>Website Category</label>
                                        <div style={{ fontSize: '14px', color: '#333', fontWeight: '500' }}>
                                            {session?.domain_name || "None"}
                                        </div>
                                    </div>

                                    <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #f0f0f0' }}>
                                        <label style={{ display: 'block', fontWeight: '600', color: '#888', fontSize: '11px', textTransform: 'uppercase', marginBottom: '4px' }}>Captured At</label>
                                        <div style={{ fontSize: '14px', color: '#333', fontWeight: '500' }}>
                                            {capturedAt}
                                        </div>
                                    </div>

                                    <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #f0f0f0' }}>
                                        <label style={{ display: 'block', fontWeight: '600', color: '#888', fontSize: '11px', textTransform: 'uppercase', marginBottom: '4px' }}>Image Hash</label>
                                        <div style={{ fontSize: '12px', color: '#333', fontFamily: 'monospace', wordBreak: 'break-all', backgroundColor: '#f8f9fa', padding: '8px', borderRadius: '6px', border: '1px dashed #ddd' }}>
                                            {imageHash}
                                        </div>
                                    </div>

                                    <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #f0f0f0' }}>
                                        <label style={{ display: 'block', fontWeight: '600', color: '#888', fontSize: '11px', textTransform: 'uppercase', marginBottom: '4px' }}>Image Reference</label>
                                        <div style={{ fontSize: '12px', color: '#000EAB', wordBreak: 'break-all' }}>
                                            {displayUrl || "None"}
                                        </div>
                                    </div>

                                    <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #f0f0f0' }}>
                                        <label style={{ display: 'block', fontWeight: '600', color: '#888', fontSize: '11px', textTransform: 'uppercase', marginBottom: '4px' }}>Encryption Type</label>
                                        <div style={{ fontSize: '14px', color: '#333', fontWeight: '500' }}>
                                            Google-managed (AES-256 encryption)
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Side: User Journey */}
                            <div style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                position: 'relative',
                                backgroundColor: '#fff'
                            }}>
                                <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a1a', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ width: '4px', height: '16px', backgroundColor: '#059669', borderRadius: '2px' }}></span>
                                        User Journey Flow
                                    </h4>
                                </div>

                                <div style={{ flex: 1, position: 'relative' }}>
                                    {(() => {
                                        if (isNavigationLoading) {
                                            return (
                                                <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', gap: '12px' }}>
                                                    <div className="spinner" style={{ border: '4px solid #f3f3f3', borderTop: '4px solid #000EAB', borderRadius: '50%', width: '30px', height: '30px', animation: 'spin 2s linear infinite' }}></div>
                                                    <p style={{ fontSize: '14px' }}>Loading journey data...</p>
                                                    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                                                </div>
                                            );
                                        }

                                        if (!selectedJourneyNavigation || selectedJourneyNavigation.length === 0) {
                                            return (
                                                <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', gap: '12px' }}>
                                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                        <path d="M9 17l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                    <p style={{ fontSize: '14px' }}>No navigation data found for this journey</p>
                                                </div>
                                            );
                                        }

                                        const captureIndex = selectedJourneyNavigation.findIndex(nav =>
                                            (nav.image_id && nav.image_id === selectedNode?.image_id) ||
                                            (nav.url && (displayUrl === nav.url)) ||
                                            (nav.to && (displayUrl === nav.to)) ||
                                            (nav.from && (displayUrl === nav.from))
                                        );
                                        const idx = captureIndex === -1 ? 0 : captureIndex;
                                        const startIndex = Math.max(0, idx - 2);
                                        const visibleNavigation = selectedJourneyNavigation.slice(startIndex, idx + 4);

                                        return (
                                            <ReactFlow
                                                nodes={visibleNavigation.map((nav, relativeIndex) => {
                                                    const originalIndex = startIndex + relativeIndex;
                                                    const isImageCapturePoint = originalIndex === idx;

                                                    let label = nav.page_name || nav.action || "";
                                                    if (!label) {
                                                        if (nav.from && nav.to) label = `From: ${nav.from}\nTo: ${nav.to}`;
                                                        else if (nav.url) label = `URL: ${nav.url}`;
                                                        else label = `Step ${originalIndex + 1}`;
                                                    }

                                                    if (nav.timestamp) {
                                                        try {
                                                            const date = new Date(nav.timestamp);
                                                            label += `\n${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                                                        } catch (e) { }
                                                    }

                                                    return {
                                                        id: `nav-node-${originalIndex}`,
                                                        data: {
                                                            label: (
                                                                <div style={{ textAlign: 'left', fontSize: '11px', width: '100%', wordBreak: 'break-word' }}>
                                                                    {label.split('\n').map((line, i) => (
                                                                        <div key={i} style={{ fontWeight: i === 0 ? '700' : '400', marginBottom: '2px' }}>{line}</div>
                                                                    ))}
                                                                </div>
                                                            )
                                                        },
                                                        position: { x: 50, y: relativeIndex * 120 + 20 },
                                                        style: {
                                                            width: 380,
                                                            padding: '12px',
                                                            borderRadius: '10px',
                                                            border: isImageCapturePoint ? '2px solid #000EAB' : '1px solid #e0e0e0',
                                                            backgroundColor: isImageCapturePoint ? '#EEF2FF' : '#fff',
                                                            boxShadow: isImageCapturePoint ? '0 4px 12px rgba(0, 14, 171, 0.15)' : '0 2px 4px rgba(0,0,0,0.05)',
                                                        },
                                                    };
                                                })}
                                                edges={visibleNavigation.slice(0, -1).map((_, relativeIndex) => ({
                                                    id: `nav-edge-${startIndex + relativeIndex}`,
                                                    source: `nav-node-${startIndex + relativeIndex}`,
                                                    target: `nav-node-${startIndex + relativeIndex + 1}`,
                                                    type: 'smoothstep',
                                                    style: { stroke: '#CBD5E1', strokeWidth: 2 },
                                                    markerEnd: { type: 'arrowclosed', color: '#CBD5E1' },
                                                }))}
                                                fitView
                                                style={{ height: '100%', outline: 'none' }}
                                                nodesConnectable={false}
                                                nodesDraggable={false}
                                                elementsSelectable={false}
                                                proOptions={{ hideAttribution: true }}
                                            >
                                                <Background color="#f1f5f9" gap={20} />
                                                <Controls showInteractive={false} style={{ bottom: 20, right: 20 }} />
                                            </ReactFlow>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>

                        <div style={{ padding: '16px 24px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', backgroundColor: '#fff', gap: '12px' }}>
                            <button
                                className="img-props-btn secondary"
                                onClick={handleExportProperties}
                                disabled={isExportPDFLoading}
                            >
                                {isExportPDFLoading ? "Generating..." : "🖨️ Export PDF"}
                            </button>
                            <button className="img-props-btn primary" onClick={closePopup}>Close View</button>
                        </div>
                    </div>
                </div>
            );
        }
        if (type === 'detailed-view') {
            const pData = processingData;
            const agentsData = pData?.processed_agents_metadata || pData;
            const timingData = pData?.timing?.agent_process_times_seconds || agentsData?.timing?.agent_process_times_seconds || {};
            const overallTiming = pData?.timing?.overall_process_time_seconds || agentsData?.timing?.overall_process_time_seconds || 0;
            const selectedModel = agentsData?.selected_model || "Unknown Model";
            const changes = processingData?.review_changes || [];

            const AGENT_ORDER = [
                "agent_1_ui_extraction",
                "perplexity_patterns",
                "agent_2_detection",
                "agent_3_classification",
                "agent_4_validation",
                "agent_5_highlights",
                "agent_6_harm_assessment",
                "agent_7_outcome_validation",
                "agent_8_fact_checker"
            ];

            const orderedAgentsData = [];
            if (agentsData) {
                AGENT_ORDER.forEach(key => {
                    if (agentsData[key] !== undefined) {
                        orderedAgentsData.push({
                            name: key,
                            data: agentsData[key],
                            time: timingData[key] ? timingData[key].toFixed(2) + "s" : "N/A"
                        });
                    }
                });
            }

            return (
                <div className="modal-overlay" onClick={closePopup} style={{ backgroundColor: "rgba(0,0,0,0.8)" }}>
                    <div className="image-properties-modal" onClick={e => e.stopPropagation()} style={{
                        backgroundColor: '#fff', borderRadius: '16px', width: '90%', height: '85%',
                        maxWidth: '1200px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                        display: 'flex', flexDirection: 'column', overflow: 'hidden'
                    }}>
                        <div className="img-props-header" style={{ padding: '20px 24px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#1a1a1a' }}>Processing Data & History</h3>
                            <button className="img-props-close" onClick={closePopup} style={{ background: '#f5f5f5', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666', width: '36px', height: '36px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&times;</button>
                        </div>

                        <div style={{ flex: 1, padding: '24px', overflowY: 'auto', backgroundColor: '#fafafa' }}>
                            {isProcessingDataLoading ? (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Loading data...</div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

                                    {/* Processed Agents Metadata */}
                                    <details open style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #eee' }}>
                                        <summary style={{ margin: '0', color: '#000EAB', fontSize: '16px', fontWeight: '600', cursor: 'pointer', outline: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span>Processed Agents Metadata</span>
                                            <div style={{ display: 'flex', gap: '12px' }}>
                                                <span style={{ fontSize: '13px', backgroundColor: '#f1f5f9', padding: '4px 8px', borderRadius: '6px', color: '#475569' }}>
                                                    🤖 Model: {selectedModel}
                                                </span>
                                                <span style={{ fontSize: '13px', backgroundColor: '#eef2ff', padding: '4px 8px', borderRadius: '6px', color: '#000eab' }}>
                                                    ⏱ Total Time: {overallTiming.toFixed(2)}s
                                                </span>
                                            </div>
                                        </summary>
                                        <div style={{ marginTop: '16px' }}>
                                            {!orderedAgentsData.length ? (
                                                <div style={{ fontSize: '14px', color: '#666' }}>No agents metadata available.</div>
                                            ) : (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                                    {orderedAgentsData.map((agent, idx) => (
                                                        <details key={idx} open style={{ backgroundColor: '#f5f7fa', padding: '16px', borderRadius: '8px', borderLeft: '4px solid #000EAB' }}>
                                                            <summary style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', outline: 'none' }}>
                                                                <div style={{ fontWeight: '600', color: '#1a1a1a', textTransform: 'capitalize', fontSize: '15px' }}>
                                                                    {idx + 1}. {agent.name.replace(/_/g, ' ')}
                                                                </div>
                                                                <div style={{ fontSize: '12px', color: '#059669', backgroundColor: '#d1fae5', padding: '4px 8px', borderRadius: '4px', fontWeight: '500' }}>
                                                                    ⏱ {agent.time}
                                                                </div>
                                                            </summary>
                                                            <div style={{ backgroundColor: '#fff', padding: '16px', borderRadius: '6px', border: '1px solid #e5e7eb', marginTop: '12px', overflowX: 'auto' }}>
                                                                {(() => {
                                                                    const renderNode = (val) => {
                                                                        if (val === null) return <span style={{ color: '#9ca3af' }}>null</span>;
                                                                        if (typeof val === 'boolean') return <span style={{ color: '#c026d3' }}>{val ? 'true' : 'false'}</span>;
                                                                        if (typeof val === 'number') return <span style={{ color: '#2563eb' }}>{val}</span>;
                                                                        if (typeof val === 'string') return <span style={{ color: '#16a34a' }}>"{val}"</span>;
                                                                        if (Array.isArray(val)) {
                                                                            if (val.length === 0) return <span style={{ color: '#9ca3af' }}>[]</span>;
                                                                            return (
                                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: '12px', borderLeft: '2px solid #e2e8f0', marginTop: '4px' }}>
                                                                                    {val.map((item, i) => <div key={i}>{renderNode(item)}</div>)}
                                                                                </div>
                                                                            );
                                                                        }
                                                                        const keys = Object.keys(val);
                                                                        if (keys.length === 0) return <span style={{ color: '#9ca3af' }}>{"{}"}</span>;
                                                                        return (
                                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', paddingLeft: '12px', borderLeft: '2px solid #e2e8f0', marginTop: '4px' }}>
                                                                                {keys.map(k => (
                                                                                    <div key={k}>
                                                                                        <span style={{ fontWeight: '600', color: '#475569' }}>{k}: </span>
                                                                                        {renderNode(val[k])}
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        );
                                                                    };
                                                                    return <div style={{ fontSize: '13px', fontFamily: 'monospace', color: '#333' }}>{renderNode(agent.data)}</div>;
                                                                })()}
                                                            </div>
                                                        </details>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </details>

                                    {/* Agent Traces Logs */}
                                    <details open style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #eee' }}>
                                        <summary style={{ margin: '0', color: '#9333ea', fontSize: '16px', fontWeight: '600', cursor: 'pointer', outline: 'none' }}>
                                            Agent Traces (Database Logs)
                                        </summary>
                                        <div style={{ marginTop: '16px' }}>
                                            {!agentTraces || agentTraces.length === 0 ? (
                                                <div style={{ fontSize: '14px', color: '#666' }}>No agent traces found for this image.</div>
                                            ) : (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                                    {agentTraces.map((trace, idx) => (
                                                        <details key={trace.trace_id || idx} style={{ backgroundColor: '#faf5ff', padding: '16px', borderRadius: '8px', borderLeft: '4px solid #9333ea' }}>
                                                            <summary style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', outline: 'none' }}>
                                                                <div style={{ fontWeight: '600', color: '#1a1a1a', textTransform: 'capitalize', fontSize: '15px' }}>
                                                                    {idx + 1}. {trace.agent_name?.replace(/_/g, ' ') || "Unknown Agent"}
                                                                </div>
                                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                                    <span style={{ fontSize: '12px', color: '#9333ea', backgroundColor: '#f3e8ff', padding: '4px 8px', borderRadius: '4px', fontWeight: '500' }}>
                                                                        🤖 {trace.model_used || "N/A"}
                                                                    </span>
                                                                    <span style={{ fontSize: '12px', color: '#059669', backgroundColor: '#d1fae5', padding: '4px 8px', borderRadius: '4px', fontWeight: '500' }}>
                                                                        ⏱ {trace.time_taken ? trace.time_taken.toFixed(2) + "s" : "N/A"}
                                                                    </span>
                                                                </div>
                                                            </summary>
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
                                                                <div style={{ backgroundColor: '#fff', padding: '12px', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                                                                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', marginBottom: '4px' }}>System Base</div>
                                                                    <div style={{ fontSize: '13px', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>{trace.system_base || "N/A"}</div>
                                                                </div>
                                                                <div style={{ backgroundColor: '#fff', padding: '12px', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                                                                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', marginBottom: '4px' }}>Instruction Base</div>
                                                                    <div style={{ fontSize: '13px', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>{trace.instruction_base || "N/A"}</div>
                                                                </div>
                                                                <div style={{ backgroundColor: '#fff', padding: '12px', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                                                                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', marginBottom: '4px' }}>Reasoning Input</div>
                                                                    <div style={{ fontSize: '13px', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>{trace.reasoning_prompts_input || "N/A"}</div>
                                                                </div>
                                                                <div style={{ backgroundColor: '#fff', padding: '12px', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                                                                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', marginBottom: '4px' }}>Human Prompt</div>
                                                                    <div style={{ fontSize: '13px', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>{trace.human_prompt || "N/A"}</div>
                                                                </div>
                                                                {/* trace.memory is omitted here as it can be very large */}
                                                                <div style={{ backgroundColor: '#fff', padding: '12px', borderRadius: '6px', border: '1px solid #e5e7eb', display: 'flex', gap: '16px' }}>
                                                                    <div>
                                                                        <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', marginBottom: '4px' }}>Vision Based</div>
                                                                        <div style={{ fontSize: '13px' }}>{trace.vision_based ? "Yes" : "No"}</div>
                                                                    </div>
                                                                    {trace.image_url && (
                                                                        <div>
                                                                            <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', marginBottom: '4px' }}>Image URL</div>
                                                                            <a href={trace.image_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: '#3b82f6', textDecoration: 'none' }}>View Image</a>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </details>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </details>

                                    {/* Review Changes */}
                                    <details open style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #eee' }}>
                                        <summary style={{ margin: '0', color: '#000EAB', fontSize: '16px', fontWeight: '600', cursor: 'pointer', outline: 'none' }}>
                                            Review Change Logs
                                        </summary>
                                        <div style={{ marginTop: '16px' }}>
                                            {changes.length === 0 ? (
                                                <div style={{ fontSize: '14px', color: '#666' }}>No changes recorded yet.</div>
                                            ) : (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                    {changes.map((log, idx) => {
                                                        const date = new Date(log.created_at).toLocaleString();
                                                        let desc = `${log.action} on ${log.field_name}: ${log.old_value || 'None'} ➔ ${log.new_value || 'None'}`;

                                                        if (log.details) {
                                                            if (log.action === 'add_law') {
                                                                desc = `Added Law: ${log.details.law_name} (${log.details.jurisdiction})`;
                                                            } else if (log.action === 'add_dark_pattern') {
                                                                desc = `Added Finding: ${log.details.dark_pattern_type}`;
                                                            }
                                                        } else if (log.parent_details) {
                                                            if (log.action === 'update_dark_pattern') {
                                                                desc = `Updated Finding (${log.parent_details.dark_pattern_type}) field ${log.field_name}: ${log.old_value || 'None'} ➔ ${log.new_value || 'None'}`;
                                                            } else if (log.action === 'update_law') {
                                                                desc = `Updated Law (${log.parent_details.law_name}) field ${log.field_name}: ${log.old_value || 'None'} ➔ ${log.new_value || 'None'}`;
                                                            }
                                                        }

                                                        return (
                                                            <div key={idx} style={{ padding: '12px', borderLeft: '3px solid #000EAB', backgroundColor: '#f9f9f9', borderRadius: '0 8px 8px 0' }}>
                                                                <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>{date}</div>
                                                                <div style={{ fontSize: '14px', color: '#333' }}>{desc}</div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </details>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
        }
        if (type === 'laws') {
            const point = data || {};
            const darkPattern = point.dark_pattern_type || point.dark_pattern || "—";
            const filteredLaws = lawsFilterJurisdiction
                ? lawsModalLaws.filter(l => (l.jurisdiction || "N/A") === lawsFilterJurisdiction)
                : lawsModalLaws;

            return (
                <div className="modal-overlay" onClick={closePopup} style={{ backgroundColor: "rgba(0,0,0,0.8)" }}>
                    <div className="laws-management-modal" onClick={e => e.stopPropagation()}>
                        <div className="laws-modal-header">
                            <div>
                                <h3 className="laws-modal-title">Laws Management</h3>
                                <span className="laws-dark-pattern-pill">Dark Pattern: {darkPattern}</span>
                            </div>
                            <div className="laws-header-actions">
                                <button
                                    className="laws-icon-btn add"
                                    onClick={() => {
                                        setIsAddingLaw(true);
                                        fetchLawsWithRanking();
                                    }}
                                    title="Add new law"
                                >
                                    ➕
                                </button>
                                <button className="laws-close-btn" onClick={closePopup} title="Close">✕</button>
                            </div>
                        </div>

                        {/* Add Law Form integration from NewEditor */}
                        {isAddingLaw && (
                            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px', border: '1px solid #ddd' }}>
                                <div style={{ marginBottom: '15px' }}>
                                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Select Law:</label>
                                    <select
                                        className="form-control"
                                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                                        value={selectedLawId}
                                        onChange={(e) => {
                                            setSelectedLawId(e.target.value);
                                            setSelectedClause('');
                                            if (e.target.value) { fetchClausesForLaw(e.target.value); }
                                        }}
                                    >
                                        <option value="">Select a law...</option>
                                        {lawsList.map(law => (
                                            <option key={law.id} value={law.id}>
                                                {law.shortName}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {selectedLawId && (
                                    <>
                                        <div style={{ marginBottom: '15px' }}>
                                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Jurisdiction:</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                                                value={selectedJurisdiction}
                                                onChange={(e) => setSelectedJurisdiction(e.target.value)}
                                                placeholder="Jurisdiction information"
                                            />
                                        </div>

                                        <div style={{ marginBottom: '15px' }}>
                                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Select Clause/Directive/Article:</label>
                                            <select
                                                className="form-control"
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                                                value={selectedClause}
                                                onChange={(e) => {
                                                    const clauseId = e.target.value;
                                                    setSelectedClause(clauseId);
                                                    const selectedClauseData = clausesList.find(c => c.id === clauseId);
                                                    if (selectedClauseData && (selectedClauseData.link_to_passage || selectedClauseData.link)) {
                                                        setSelectedLink(selectedClauseData.link_to_passage || selectedClauseData.link);
                                                    } else { setSelectedLink(''); }
                                                }}
                                            >
                                                <option value="">Select a clause...</option>
                                                {clausesList.map(clause => (
                                                    <option key={clause.id} value={clause.id}>
                                                        {clause.clause}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div style={{ marginBottom: '15px' }}>
                                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Link to Passage:</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#eee' }}
                                                value={selectedLink}
                                                onChange={(e) => setSelectedLink(e.target.value)}
                                                placeholder="Link will be auto-populated"
                                                readOnly={true}
                                            />
                                        </div>
                                    </>
                                )}

                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                    <button
                                        className="laws-save-btn"
                                        style={{ background: '#059669' }}
                                        disabled={!selectedLawId || !selectedClause}
                                        onClick={async () => {
                                            const law = lawsList.find(l => l.id === selectedLawId);
                                            const clause = clausesList.find(c => c.id === selectedClause);
                                            if (law && clause) {
                                                try {
                                                    const res = await addLawAPI({
                                                        dark_pattern_id: data?.dark_pattern_id,
                                                        law_name: law.shortName,
                                                        jurisdiction: selectedJurisdiction,
                                                        clause_name: clause.clause,
                                                        link: selectedLink || clause.link || '',
                                                        correctness_type: 'correct'
                                                    });

                                                    if (res?.status === "success" && res.law) {
                                                        const newLaw = {
                                                            id: res.law.law_id,
                                                            realId: res.law.law_id,
                                                            law: res.law.law_name,
                                                            jurisdiction: res.law.jurisdiction,
                                                            clause: res.law.clause_name,
                                                            link: res.law.source_url || selectedLink || clause.link || '',
                                                            correctness_type: normalizeCorrectnessType(res.law.correctness_type)
                                                        };
                                                        setLawsModalLaws(prev => [...prev, newLaw]);
                                                        showMsg("Law added successfully.");
                                                        loadData();
                                                    }
                                                } catch (err) {
                                                    console.error("Backend add law failed", err);
                                                    showMsg("Failed to add law record.", true);
                                                }
                                            }
                                            setIsAddingLaw(false); setSelectedLawId(''); setSelectedClause(''); setSelectedLink(''); setSelectedJurisdiction('');
                                        }}
                                    >
                                        Add Law
                                    </button>
                                    <button
                                        className="outline-btn danger-btn"
                                        onClick={() => {
                                            setIsAddingLaw(false); setSelectedLawId(''); setSelectedClause(''); setSelectedLink(''); setSelectedJurisdiction('');
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}


                        <div className="laws-instructions">
                            <strong>Instructions:</strong> The user can change the status of each law by selecting ‘Correct’ if the identified law is correct, or ‘Incorrect’ if the identified law is incorrect.
                        </div>

                        <div className="laws-filter-bar">
                            <span>🔍 Filter by Jurisdiction:</span>
                            <select value={lawsFilterJurisdiction} onChange={e => setLawsFilterJurisdiction(e.target.value)}>
                                <option value="">All Jurisdictions</option>
                                {[...new Set(lawsModalLaws.map(l => l.jurisdiction || "N/A"))].sort().map(j => (
                                    <option key={j} value={j}>{j}</option>
                                ))}
                            </select>
                        </div>

                        <div className="laws-table-wrap">
                            <table className="laws-management-table">
                                <thead>
                                    <tr>
                                        <th>Law</th>
                                        <th>Jurisdiction</th>
                                        <th>Instructions</th>
                                        <th>Action</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredLaws.map((law, idx) => (
                                        <tr key={law.id} className={idx % 2 === 0 ? "" : "alt"}>
                                            <td>{law.law}</td>
                                            <td>{law.jurisdiction || "N/A"}</td>
                                            <td>{law.clause}</td>
                                            <td>
                                                {law.link ? (
                                                    <button className="laws-action-eye" onClick={() => window.open(law.link, "_blank")} title="View passage">
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                                                    </button>
                                                ) : (
                                                    <span className="laws-no-link">No link</span>
                                                )}
                                            </td>
                                            <td>
                                                <select
                                                    value={normalizeCorrectnessType(law.correctness_type)}
                                                    onChange={(e) => {
                                                        if (!e.target.value) return;
                                                        handleUpdateLawStatus(law.realId, e.target.value);
                                                    }}
                                                    className="law-correctness-select"
                                                >
                                                    <option value="">Select option</option>
                                                    <option value="correct">Correct</option>
                                                    <option value="incorrect">Incorrect</option>
                                                    <option value="redundant">Redundant</option>
                                                    <option value="duplicate">Duplicate</option>
                                                </select>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="modal-overlay" onClick={closePopup}>
                <div className="modal-container-large" onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                        <h3>{type.toUpperCase()}</h3>
                        <button className="modal-close-btn" onClick={closePopup}>&times;</button>
                    </div>
                    <div className="modal-content">

                    </div>
                </div>
            </div>
        );
    };

    const renderResponseCards = () => {
        const visiblePoints = points.filter((p) => p.dark_pattern_type !== null);
        return (
            <div className="response-cards-list">
                {visiblePoints.map((p, idx) => {
                    const isEditing = editingDPId === p.dark_pattern_id;
                    const draft = isEditing ? (editingDraft || { response_type: p.response_type || "outcome", priority: normalizePriority(p.priority), dark_pattern_type: p.dark_pattern_type || "", dark_pattern_description: p.dark_pattern_description || "" }) : null;
                    return (
                        <div className={`finding-card ${isEditing ? 'editing' : ''}`} key={p.dark_pattern_id || idx}>
                            <div className="card-header">
                                <div className="header-top">
                                    <div className="header-top-left">
                                        {isEditing ? (
                                            <>
                                                <select
                                                    className="response-type-select"
                                                    value={draft.response_type}
                                                    onChange={(e) => setEditingDraft(prev => ({ ...prev, response_type: e.target.value }))}
                                                >
                                                    <option value="outcome">Outcome</option>
                                                    <option value="hallucination">Hallucination</option>
                                                    <option value="doubtful">Doubtful</option>
                                                    <option value="ignore">Ignore</option>
                                                </select>
                                                <select
                                                    className="priority-select"
                                                    value={draft.priority}
                                                    onChange={(e) => setEditingDraft(prev => ({ ...prev, priority: e.target.value }))}
                                                >
                                                    <option value="1">High Priority</option>
                                                    <option value="2">High</option>
                                                    <option value="3">Medium</option>
                                                    <option value="4">Low</option>
                                                    <option value="5">Lowest</option>
                                                </select>
                                                <select
                                                    className="dp-type-select"
                                                    value={draft.dark_pattern_type}
                                                    onChange={(e) => setEditingDraft(prev => ({ ...prev, dark_pattern_type: e.target.value }))}
                                                >
                                                    <option value="">{isDarkPatternsLoading ? "Loading patterns..." : "Select Pattern"}</option>
                                                    {darkPatterns.map(dp => (
                                                        <option key={dp.id} value={dp.name}>{dp.name}</option>
                                                    ))}
                                                </select>
                                            </>
                                        ) : (
                                            <>
                                                <span className="badge response-type-badge">{p.response_type || "outcome"}</span>
                                                <span
                                                    className="badge priority-badge"
                                                    style={{
                                                        backgroundColor: (PRIORITY_COLORS[PRIORITY_LABELS[p.priority]] || PRIORITY_COLORS[p.priority] || PRIORITY_COLORS['Medium']).bg,
                                                        color: (PRIORITY_COLORS[PRIORITY_LABELS[p.priority]] || PRIORITY_COLORS[p.priority] || PRIORITY_COLORS['Medium']).text,
                                                    }}
                                                >
                                                    {PRIORITY_LABELS[p.priority] || p.priority || "Medium"}
                                                </span>
                                                <span className="badge dark-pattern-badge">{p.dark_pattern_type || "Finding"}</span>
                                            </>
                                        )}
                                    </div>
                                    <div className="header-top-right">
                                        <button
                                            className={`edit-chip-btn ${isEditing ? 'active' : ''}`}
                                            onClick={() => isEditing ? handleCancelEdit() : handleStartEdit(p)}
                                            title={isEditing ? "Cancel edit" : "Edit finding"}
                                        >
                                            ✏️
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="finding-description-block">
                                <span className="finding-desc-title">{isEditing ? (draft.dark_pattern_type || "Finding") : (p.dark_pattern_type || "Finding")}</span>
                                <textarea
                                    className="finding-description"
                                    value={isEditing ? draft.dark_pattern_description : (p.dark_pattern_description || "")}
                                    onChange={isEditing ? (e) => setEditingDraft(prev => ({ ...prev, dark_pattern_description: e.target.value })) : undefined}
                                    readOnly={!isEditing}
                                />
                            </div>
                            <div className="card-footer">
                                <button className="link-btn" onClick={() => openPopup('laws', p)}>⚖ View Laws</button>
                                <button className="link-btn" onClick={() => openPopup('recs', p)}>💡 Recommendations</button>

                                <div className="footer-action-buttons">
                                    {isEditing ? (
                                        <>
                                            <button className="outline-btn danger-btn" onClick={handleCancelEdit} disabled={isProcessing}>Cancel changes</button>
                                            <button className="outline-btn success-btn" onClick={handleSaveEdit} disabled={isProcessing}>{isProcessing ? 'Saving...' : 'Save changes'}</button>
                                        </>
                                    ) : (
                                        <>
                                            <button className="outline-btn danger-btn" onClick={() => handleRevokeDP(p.dark_pattern_id)}>Remove</button>
                                            <button className="outline-btn success-btn" onClick={() => handleConfirmDP(p.dark_pattern_id)}>Confirm</button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
                {visiblePoints.length === 0 && <div className="no-findings">No dark patterns detected.</div>}
            </div>
        );
    };

    if (loading && !session) return <div className="loading-screen" style={{ paddingTop: 120, textAlign: 'center' }}>Loading Session Data...</div>;
    if (error) return <div className="error-screen" style={{ paddingTop: 120, textAlign: 'center', color: '#d92d20' }}>{error}</div>;
    if (!session) return <div className="loading-screen" style={{ paddingTop: 120, textAlign: 'center' }}>Initializing Session...</div>;

    return (
        <div className="session-viewer-container">
            <ClientHeader user={user} onLogout={onLogout} />
            {/* 2. Session Header */}
            <header className="viewer-header cr-header">
                <div className="header-left">
                    <div className="title-group">
                        <h1>{session?.session_name || "Session Review"}</h1>
                        <span className="domain-tag">{session?.domain_name}</span>
                    </div>
                </div>
                {actionMsg && <div className="status-toast">{actionMsg}</div>}
            </header>

            {/* 2. Tab Navigation */}
            <div className="viewer-tabs">
                <div className="viewer-tabs-left">
                    <button
                        className={activeTab === "all-screens" ? "active" : ""}
                        onClick={() => setActiveTab("all-screens")}
                    >
                        All screens
                    </button>
                    <button
                        className={activeTab === "detailed-review" ? "active" : ""}
                        onClick={() => setActiveTab("detailed-review")}
                    >
                        Detailed review
                    </button>
                </div>
                <div className="viewer-tabs-right">
                    <button className="viewer-tab-action-btn" onClick={() => navigate('/sessions')}>
                        ← Back
                    </button>
                    <button className="viewer-tab-action-btn" onClick={loadData}>
                        ↻ Refresh
                    </button>
                    {getOrgId() === process.env.REACT_APP_ADMIN_ORG_ID && (
                        <button className="viewer-tab-action-btn viewer-tab-summary-btn" onClick={handleOpenSummaryModal}>
                            📋 Session Summary
                        </button>
                    )}
                </div>
            </div>

            {/* 3. Main Content Content */}
            <main className="viewer-content">
                {activeTab === "all-screens" ? (
                    <div className="flow-wrapper">
                        <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            onNodesChange={onNodesChange}
                            onEdgesChange={onEdgesChange}
                            onNodeClick={handleNodeClick}
                            style={{ height: "100%" }}
                            fitView
                            attributionPosition="bottom-right"
                            minZoom={0.1}
                            maxZoom={4}
                            defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
                        >
                            <Background color="#eaecf0" gap={20} />
                            <Controls />
                        </ReactFlow>
                    </div>
                ) : (
                    <div className="split-view-container" ref={containerRef}>
                        {/* Image Side - 3 Parts: 1) Nav 2) Image 3) Footer Links */}
                        <div className="image-panel" style={{ flex: 1 }}>
                            {/* Part 1: Previous / Next navigation */}
                            <div className="image-panel-part1 image-nav-bar">
                                <div className="nav-info">
                                    {/* <span className="nav-index">Image {currentImageIndex + 1} of {currentJourney?.images?.length || 0}</span> */}
                                    <span className="nav-index">Image {globalImageIndex} of {totalImagesCount}</span>
                                    <span className="nav-divider">|</span>
                                    <span className="nav-journey">{currentJourney?.user_journey_name}</span>
                                    <span className="nav-divider">|</span>
                                    <span className="nav-page">{currentImage?.page_name}</span>
                                </div>
                                <div className="nav-controls">
                                    <button
                                        onClick={handlePrev}
                                        disabled={currentImageIndex === 0 && currentJourneyIndex === 0}
                                    >
                                        ← Previous
                                    </button>
                                    <button
                                        onClick={handleNext}
                                        disabled={currentJourneyIndex === journeys.length - 1 && currentImageIndex === (currentJourney?.images?.length || 0) - 1}
                                    >
                                        Next →
                                    </button>
                                </div>
                            </div>

                            {/* Part 2: Image */}
                            <div
                                className="image-panel-part2 image-viewport"
                                onWheel={handleWheel}
                                onMouseDown={handleMouseDown}
                                onMouseMove={handleMouseMove}
                                onMouseUp={handleMouseUp}
                                onMouseLeave={handleMouseUp}
                            >
                                <div
                                    className="image-transform-layer"
                                    style={{
                                        transform: `translate(${imagePos.x}px, ${imagePos.y}px) scale(${zoomLevel})`
                                    }}
                                >
                                    {(() => {
                                        let gallery = [currentImage?.image_url];
                                        if (currentImage?.additional_image_urls) {
                                            try {
                                                const additional = typeof currentImage.additional_image_urls === 'string'
                                                    ? JSON.parse(currentImage.additional_image_urls)
                                                    : currentImage.additional_image_urls;
                                                const additionalArray = Array.isArray(additional) ? additional : [];
                                                gallery = [...gallery, ...additionalArray];
                                            } catch (e) {
                                                console.error("Failed to parse additional images", e);
                                            }
                                        }
                                        const activeImg = gallery[selectedGalleryImageIndex] || currentImage?.image_url;
                                        return <img src={activeImg} alt="Screen" draggable={false} />;
                                    })()}
                                </div>
                                {/* <div className="viewer-toolbar">
                                    <div className="mode-btns">
                                        <button className={viewMode === "standard" ? "active" : ""} onClick={() => setViewMode("standard")}>Standard</button>
                                        <button className={viewMode === "heatmap" ? "active" : ""} onClick={() => setViewMode("heatmap")}>Heatmap</button>
                                        <button className={viewMode === "scanpath" ? "active" : ""} onClick={() => setViewMode("scanpath")}>Scanpath</button>
                                    </div>
                                    <div className="zoom-actions">
                                        <button onClick={() => setZoomLevel(z => Math.max(z - 0.2, 0.5))}>-</button>
                                        <span className="zoom-val">{Math.round(zoomLevel * 100)}%</span>
                                        <button onClick={() => setZoomLevel(z => Math.min(z + 0.2, 5))}>+</button>
                                    </div>
                                </div> */}
                            </div>

                            {/* Additional Images Selector if exists */}
                            {(() => {
                                let gallery = [currentImage?.image_url];
                                if (currentImage?.additional_image_urls) {
                                    try {
                                        const additional = typeof currentImage.additional_image_urls === 'string'
                                            ? JSON.parse(currentImage.additional_image_urls)
                                            : currentImage.additional_image_urls;
                                        const additionalArray = Array.isArray(additional) ? additional : [];
                                        gallery = [...gallery, ...additionalArray];
                                    } catch (e) { }
                                }

                                if (gallery.length <= 1) return null;

                                return (
                                    <div className="additional-images-selector">
                                        {gallery.map((url, idx) => (
                                            <div
                                                key={idx}
                                                className={`thumb-item ${selectedGalleryImageIndex === idx ? 'active' : ''}`}
                                                onClick={() => setSelectedGalleryImageIndex(idx)}
                                            >
                                                <img src={url} alt={`View ${idx + 1}`} />
                                            </div>
                                        ))}
                                    </div>
                                );
                            })()}

                            {/* Part 3: Image Properties & Full Screen links */}
                            <div className="image-panel-part3 image-footer-links">
                                <button className="viewer-link-btn" onClick={() => openPopup('properties')}>
                                    <span className="viewer-link-icon">📄</span>
                                    Image Properties & Journey
                                </button>
                                <button className="viewer-link-btn" onClick={() => setIsFullscreenOpen(true)}>
                                    <span className="viewer-link-icon">🔍</span>
                                    Full Screen View
                                </button>
                                {getOrgId() === process.env.REACT_APP_ADMIN_ORG_ID && (
                                    <button className="viewer-link-btn" onClick={() => openPopup('detailed-view')}>
                                        <span className="viewer-link-icon">📊</span>
                                        Detailed View
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Divider */}
                        <div className="resizer-divider" onMouseDown={() => setIsResizing(true)}></div>

                        {/* Details Side */}
                        <div className="details-panel" style={{ width: rightPanelWidth }}>
                            <div className="panel-header">
                                <div className="panel-heading-group">
                                    <h3>Compliance issue</h3>
                                    {/* <span className="panel-subtitle">
                                        Journey {currentJourneyIndex + 1} / {journeys.length || 1}
                                    </span> */}
                                </div>
                                <div className="header-actions">
                                    <button
                                        className="add-point-btn"
                                        onClick={handleAddNewPoint}
                                        title="Add new point"
                                        aria-label="Add new point"
                                    >
                                        +
                                    </button>
                                    {/* <button className="merge-btn" disabled={selectedPoints.length < 2}>Merge</button> */}
                                </div>
                            </div>
                            <div className="panel-scroll-area">
                                {renderResponseCards()}
                                {currentImage?.img_feedback && (
                                    <div className="received-feedback-section">
                                        <label>Image Feedback</label>
                                        <div className="received-feedback-content">
                                            {currentImage.img_feedback}
                                        </div>
                                    </div>
                                )}
                                <div className="internal-feedback-section">
                                    <label>Internal Feedback</label>
                                    <textarea
                                        placeholder="Add your internal feedback here."
                                        value={internalFeedback}
                                        onChange={(e) => setInternalFeedback(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="panel-footer">
                                <button
                                    className="save-all-btn"
                                    onClick={handleSaveAll}
                                    disabled={isProcessing}
                                >
                                    {isProcessing ? "Processing..." : "Save All Changes"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>

            {renderPopups()}

            {/* ── Session Summary Modal ─────────────────────────────────── */}
            {isSummaryModalOpen && (
                <div
                    className="summary-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Session Summary"
                    onClick={() => { if (!isSummaryGenerating) setIsSummaryModalOpen(false); }}
                >
                    <div className="summary-modal" onClick={e => e.stopPropagation()}>
                        <div className="summary-modal-header">
                            <div className="summary-modal-title">
                                <span className="summary-modal-icon">📋</span>
                                <h2>High-Level Audit Summary</h2>
                                {session?.session_name && (
                                    <span className="summary-session-name">{session.session_name}</span>
                                )}
                            </div>
                            <button
                                className="summary-modal-close"
                                onClick={() => setIsSummaryModalOpen(false)}
                                aria-label="Close"
                                disabled={isSummaryGenerating}
                            >&times;</button>
                        </div>

                        <div className="summary-modal-body">
                            {isSummaryGenerating && !summaryText && (
                                <div className="summary-generating-state">
                                    <div className="summary-spinner"></div>
                                    <p>Generating summary with AI&hellip;</p>
                                </div>
                            )}

                            {summaryError && (
                                <div className="summary-error">
                                    <span>⚠ {summaryError}</span>
                                </div>
                            )}

                            {!summaryError && (summaryText || !isSummaryGenerating) && (
                                <>
                                    {isSummaryGenerating && (
                                        <p className="summary-streaming-hint">✦ Generating…</p>
                                    )}
                                    <textarea
                                        id="summary-edit-textarea"
                                        className="summary-textarea"
                                        value={summaryText}
                                        onChange={e => setSummaryText(e.target.value)}
                                        placeholder="Summary will appear here…"
                                        rows={16}
                                        readOnly={isSummaryGenerating}
                                    />
                                </>
                            )}
                        </div>

                        <div className="summary-modal-footer">
                            <span className="summary-footer-hint">
                                {isSummaryGenerating
                                    ? "AI is writing the summary…"
                                    : "You can edit the text above before downloading."}
                            </span>
                            <div className="summary-footer-actions">
                                <button
                                    className="summary-btn-secondary"
                                    onClick={() => setIsSummaryModalOpen(false)}
                                >Close</button>
                                <button
                                    className="summary-btn-primary"
                                    onClick={handleExportSummaryPDF}
                                    disabled={isSummaryGenerating || isSummaryExporting || !summaryText.trim()}
                                >
                                    {isSummaryExporting ? "Generating PDF…" : "⬇ Download as PDF"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isFullscreenOpen && (
                <div
                    className="fullscreen-overlay"
                    onClick={() => setIsFullscreenOpen(false)}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Full screen image view"
                >
                    <div className="fullscreen-modal" onClick={e => e.stopPropagation()}>
                        <div className="fullscreen-header">
                            <span className="fullscreen-title">{currentImage?.page_name || "Screen"}</span>
                            <button className="fullscreen-close" onClick={() => setIsFullscreenOpen(false)} aria-label="Close">&times;</button>
                        </div>
                        <div className="fullscreen-body" style={{ position: 'relative', height: '100%' }}>
                            {(() => {
                                let gallery = [currentImage?.image_url];
                                if (currentImage?.additional_image_urls) {
                                    try {
                                        const additional = typeof currentImage.additional_image_urls === 'string'
                                            ? JSON.parse(currentImage.additional_image_urls)
                                            : currentImage.additional_image_urls;
                                        if (Array.isArray(additional)) {
                                            gallery = [...gallery, ...additional];
                                        }
                                    } catch (e) { }
                                }
                                const activeImg = gallery[selectedGalleryImageIndex] || currentImage?.image_url;
                                return (
                                    <img
                                        src={activeImg}
                                        alt="Full screen view"
                                        style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain' }}
                                    />
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .session-viewer-container {
                    display: flex;
                    flex-direction: column;
                    height: 100vh;
                    background: #f8f9fa;
                    overflow: hidden;
                    font-family: 'Inter', sans-serif;
                }
                .viewer-header {
                    background: #f8f9fa;
                    border-bottom: 1px solid #ddd;
                    padding: 14px 20px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    z-index: 100;
                }
                .header-left { display: flex; gap: 20px; align-items: center; }
                .title-group h1 { margin: 0; font-size: 24px; color: #1a1a1a; font-weight: 600; }
                .domain-tag { font-size: 13px; color: #666; }
                
                .viewer-tabs {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 16px;
                    padding: 0 20px;
                    background: #f8f9fa;
                    border-bottom: 1px solid #ddd;
                }
                .viewer-tabs-left { display: flex; gap: 32px; }
                .viewer-tabs-right { display: flex; gap: 10px; }
                .viewer-tabs .viewer-tabs-left button {
                    background: none;
                    border: none;
                    padding: 12px 0;
                    color: #666;
                    cursor: pointer;
                    border-bottom: 3px solid transparent;
                    transition: all 0.2s;
                    font-size: 16px;
                    font-weight: 400;
                }
                .viewer-tabs .viewer-tabs-left button.active {
                    color: #000;
                    border-bottom-color: #000;
                    font-weight: 600;
                }
                .viewer-tab-action-btn {
                    background: #fff;
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    padding: 8px 16px;
                    font-size: 14px;
                    font-weight: 500;
                    color: #0032c8;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .viewer-tab-action-btn:hover {
                    background: #f8f9fa;
                    border-color: #0032c8;
                }

                .viewer-content { flex: 1; overflow: hidden; position: relative; }
                .flow-wrapper { height: 100%; width: 100%; background: #f9fafb; }

                .split-view-container { display: flex; height: 100%; width: 100%; }
                .image-panel { display: flex; flex-direction: column; min-width: 0; }
                .image-panel-part1.image-nav-bar {
                    padding: 14px 20px;
                    border-bottom: 1px solid #ddd;
                    background: #fff;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-shrink: 0;
                }
                .image-panel-part2.image-viewport {
                    flex: 1;
                    min-height: 0;
                }
                .image-panel-part3.image-footer-links {
                    padding: 14px 20px;
                    border-top: 1px solid #ddd;
                    background: #fff;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 32px;
                    flex-shrink: 0;
                }
                .image-viewport { 
                    flex: 1; width: 100%; background: #f2f4f7; 
                    position: relative; overflow: hidden; display: flex; align-items: center; justify-content: center;
                    padding: 16px;
                    box-sizing: border-box;
                    background: #f2f4f7;
                }
                .additional-images-selector {
                    padding: 12px 20px;
                    background: #fff;
                    border-top: 1px solid #eee;
                    display: flex;
                    gap: 10px;
                    overflow-x: auto;
                    scrollbar-width: thin;
                    scrollbar-color: #ddd transparent;
                }
                .additional-images-selector::-webkit-scrollbar {
                    height: 6px;
                }
                .additional-images-selector::-webkit-scrollbar-thumb {
                    background: #ddd;
                    border-radius: 10px;
                }
                .thumb-item {
                    width: 64px;
                    height: 64px;
                    border-radius: 8px;
                    border: 2px solid transparent;
                    cursor: pointer;
                    overflow: hidden;
                    flex-shrink: 0;
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                    position: relative;
                }
                .thumb-item:hover {
                    opacity: 0.9;
                    transform: translateY(-2px);
                }
                .thumb-item.active {
                    border-color: #000EAB;
                    box-shadow: 0 4px 12px rgba(0, 14, 171, 0.2);
                    opacity: 1 !important;
                }
                .thumb-item img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
                .image-transform-layer { 
                    transition: transform 0.05s ease-out; 
                    cursor: grab;
                    width: 100%;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                    transform-origin: center center;
                }
                .image-transform-layer:active { cursor: grabbing; }
                .image-transform-layer img { 
                    max-width: 100%;
                    max-height: 100%;
                    width: auto;
                    height: auto;
                    object-fit: contain;
                    border-radius: 8px; 
                    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
                }
                .overlay { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 8px; }
                .heatmap { opacity: 0.5; pointer-events: none; }

                .viewer-toolbar {
                    position: absolute; bottom: 20px; left: 50%; transform: translateX(-50%);
                    background: #fff; padding: 6px 12px; border-radius: 12px; border: 1px solid #eaecf0;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1); display: flex; gap: 16px; align-items: center; z-index: 10;
                }
                .mode-btns { display: flex; gap: 4px; background: #f2f4f7; padding: 3px; border-radius: 8px; }
                .mode-btns button { 
                    border: none; background: transparent; padding: 4px 8px; border-radius: 6px; 
                    font-size: 11px; font-weight: 700; color: #667085; cursor: pointer;
                }
                .mode-btns button.active { background: #fff; color: #101828; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }

                .viewer-link-btn {
                    background: none; border: none; color: #0032c8;
                    font-size: 14px; font-weight: 500; text-decoration: underline;
                    cursor: pointer; padding: 4px 8px;
                    display: flex; align-items: center; gap: 2px;
                }
                .viewer-link-btn:hover { color: #001a66; }
                .viewer-link-icon { font-size: 18px; line-height: 1; }

                /* ── Summary Modal ──────────────────────────────────────────── */
                .summary-overlay {
                    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(0, 0, 0, 0.6);
                    display: flex; align-items: center; justify-content: center;
                    z-index: 3000;
                    backdrop-filter: blur(4px);
                }
                .summary-modal {
                    background: #fff;
                    border-radius: 16px;
                    width: min(820px, 94vw);
                    max-height: 90vh;
                    display: flex; flex-direction: column;
                    box-shadow: 0 32px 72px rgba(0,0,0,0.28);
                    overflow: hidden;
                }
                .summary-modal-header {
                    display: flex; align-items: center; justify-content: space-between;
                    padding: 20px 24px 16px;
                    border-bottom: 2px solid #000EAB;
                    background: linear-gradient(135deg, #f0f3ff 0%, #fff 100%);
                    flex-shrink: 0;
                }
                .summary-modal-title {
                    display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
                }
                .summary-modal-title h2 {
                    margin: 0; font-size: 20px; font-weight: 700; color: #000EAB;
                }
                .summary-modal-icon { font-size: 24px; }
                .summary-session-name {
                    font-size: 13px; color: #5c6470; font-weight: 400;
                    background: #eef0ff; padding: 2px 8px; border-radius: 20px;
                }
                .summary-modal-close {
                    background: none; border: none; font-size: 26px; cursor: pointer;
                    color: #888; line-height: 1; padding: 4px; transition: color 0.15s;
                }
                .summary-modal-close:hover { color: #000EAB; }
                .summary-modal-close:disabled { opacity: 0.4; cursor: not-allowed; }

                .summary-modal-body {
                    flex: 1; overflow-y: auto; padding: 20px 24px;
                    display: flex; flex-direction: column; gap: 12px;
                }
                .summary-generating-state {
                    display: flex; flex-direction: column; align-items: center;
                    justify-content: center; padding: 48px 0; gap: 16px; color: #5c6470;
                }
                .summary-generating-state p { margin: 0; font-size: 15px; }
                .summary-spinner {
                    width: 40px; height: 40px;
                    border: 4px solid #e0e7ff;
                    border-top-color: #000EAB;
                    border-radius: 50%;
                    animation: summarySpinAnim 0.8s linear infinite;
                }
                @keyframes summarySpinAnim {
                    to { transform: rotate(360deg); }
                }
                .summary-streaming-hint {
                    margin: 0 0 4px;
                    font-size: 12px; color: #000EAB; font-weight: 500;
                    animation: summaryPulse 1.2s ease-in-out infinite;
                }
                @keyframes summaryPulse {
                    0%, 100% { opacity: 1; } 50% { opacity: 0.4; }
                }
                .summary-textarea {
                    width: 100%; min-height: 340px;
                    border: 1px solid #d1d5db; border-radius: 10px;
                    padding: 16px; font-size: 14.5px; line-height: 1.75;
                    color: #1a1a2e; font-family: inherit; resize: vertical;
                    outline: none; transition: border-color 0.15s, box-shadow 0.15s;
                    box-sizing: border-box;
                }
                .summary-textarea:focus {
                    border-color: #000EAB;
                    box-shadow: 0 0 0 3px rgba(0,14,171,0.1);
                }
                .summary-textarea[readonly] {
                    background: #fafafa; cursor: default;
                }
                .summary-error {
                    background: #fff2f2; border: 1px solid #fca5a5;
                    border-radius: 8px; padding: 14px 16px;
                    color: #b91c1c; font-size: 14px;
                }

                .summary-modal-footer {
                    display: flex; align-items: center; justify-content: space-between;
                    padding: 16px 24px;
                    border-top: 1px solid #eaecf0;
                    background: #fafafa;
                    flex-shrink: 0;
                    gap: 12px;
                }
                .summary-footer-hint {
                    font-size: 12px; color: #888; flex: 1;
                }
                .summary-footer-actions {
                    display: flex; gap: 10px; align-items: center;
                }
                .summary-btn-secondary {
                    padding: 9px 20px; border-radius: 8px;
                    border: 1px solid #d1d5db; background: #fff;
                    color: #374151; font-size: 14px; font-weight: 500;
                    cursor: pointer; transition: background 0.15s;
                }
                .summary-btn-secondary:hover { background: #f3f4f6; }
                .summary-btn-primary {
                    padding: 9px 22px; border-radius: 8px;
                    border: none; background: #000EAB;
                    color: #fff; font-size: 14px; font-weight: 600;
                    cursor: pointer; transition: background 0.15s, opacity 0.15s;
                    display: flex; align-items: center; gap: 6px;
                }
                .summary-btn-primary:hover:not(:disabled) { background: #0020d0; }
                .summary-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

                .fullscreen-overlay {
                    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(0, 0, 0, 0.75);
                    display: flex; align-items: center; justify-content: center;
                    z-index: 2000;
                }
                .fullscreen-modal {
                    background: #fff; border-radius: 12px;
                    max-width: 95vw; max-height: 95vh;
                    overflow: hidden; box-shadow: 0 24px 48px rgba(0,0,0,0.3);
                    display: flex; flex-direction: column;
                }
                .fullscreen-header {
                    padding: 14px 20px; border-bottom: 1px solid #eaecf0;
                    display: flex; justify-content: space-between; align-items: center;
                }
                .fullscreen-title { font-weight: 600; font-size: 16px; color: #101828; }
                .fullscreen-close {
                    width: 36px; height: 36px; border: none; background: transparent;
                    font-size: 28px; color: #667085; cursor: pointer;
                    border-radius: 8px; line-height: 1;
                }
                .fullscreen-close:hover { background: #f2f4f7; color: #101828; }
                .fullscreen-body {
                    padding: 24px; overflow: auto;
                    display: flex; align-items: center; justify-content: center;
                    max-height: 85vh;
                }

                .nav-index { font-size: 13px; color: #475467; font-weight: 600; }
                .nav-controls { display: flex; gap: 8px; }
                .nav-controls button {
                    padding: 6px 12px;
                    background: #000eab;
                    color: #fff;
                    border: none;
                    border-radius: 8px;
                    font-size: 12px;
                    cursor: pointer;
                }
                .nav-controls button:disabled {
                    background: #f5f5f5;
                    color: #999;
                    cursor: not-allowed;
                }
                .resizer-divider { width: 4px; cursor: col-resize; background: #ddd; transition: background 0.2s; }
                .resizer-divider:hover { background: #000eab; }

                .details-panel { background: #fff; display: flex; flex-direction: column; border-left: 1px solid #eaecf0; box-shadow: -4px 0 16px rgba(16, 24, 40, 0.04); font-family: Arial, sans-serif; }
                .panel-header { padding: 16px 20px; border-bottom: 1px solid #eaecf0; display: flex; justify-content: space-between; align-items: center; gap: 10px; }
                .panel-scroll-area { flex: 1; overflow-y: auto; padding: 20px; background: #f9fafb; }
                .panel-header h3 { margin: 0; }
                .panel-heading-group { display: flex; flex-direction: column; gap: 4px; }
                .panel-subtitle { font-size: 12px; color: #667085; font-weight: 500; }
                .header-actions { display: flex; gap: 10px; }
                .add-point-btn, .merge-btn, .save-all-btn {
                    border-radius: 8px;
                    font-weight: 600;
                    border: 1px solid #d0d5dd;
                    background: #fff;
                    color: #344054;
                    cursor: pointer;
                }
                .add-point-btn {
                    width: 44px;
                    height: 44px;
                    min-width: 44px;
                    border-radius: 999px;
                    border-color: #1d4ed8;
                    color: #1d4ed8;
                    background: #f8fbff;
                    font-size: 28px;
                    line-height: 1;
                    padding: 0;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                }
                .merge-btn { padding: 10px 14px; min-height: 44px; }
                .merge-btn:disabled { opacity: 0.6; cursor: not-allowed; }
                .add-point-btn:hover, .merge-btn:hover { background: #f9fafb; }
                
                .finding-card { background: #fff; border: 1px solid #d0d5dd; border-radius: 12px; padding: 0; margin-bottom: 16px; box-shadow: 0 2px 8px rgba(16, 24, 40, 0.06); overflow: hidden; font-family: Arial, sans-serif; }
                .card-header { padding: 14px 18px; border-bottom: 1px solid #eaecf0; background: #fff; }
                .card-header .header-top { display: flex; justify-content: space-between; align-items: center; gap: 10px; }
                .header-top-left { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; flex: 1; }
                .header-top-right { display: flex; align-items: center; justify-content: flex-end; }
                .priority-badge {
                    /* colors set inline based on priority */
                    border: none;
                    border-radius: 999px;
                    font-size: 12px;
                    font-weight: 500;
                    padding: 8px 14px;
                }
                .response-type-badge {
                    background: #e0f2fe;
                    color: #0369a1;
                    border: none;
                    border-radius: 999px;
                    font-size: 12px;
                    font-weight: 500;
                    padding: 8px 14px;
                }
                .dark-pattern-badge {
                    background: #f4ebff;
                    color: #6b21a8;
                    border: none;
                    border-radius: 999px;
                    font-size: 12px;
                    font-weight: 500;
                    padding: 8px 14px;
                }
                .edit-chip-btn {
                    width: 32px;
                    height: 32px;
                    min-width: 32px;
                    padding: 6px 12px;
                    border-radius: 50%;
                    border: 1px solid #000EAB;
                    background: transparent;
                    color: #000EAB;
                    font-size: 14px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .edit-chip-btn:hover { opacity: 0.85; }
                .finding-description-block {
                    padding: 14px 18px;
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }
                .finding-desc-title {
                    align-self: flex-start;
                    font-weight: 700;
                    font-size: 14px;
                    color: #101828;
                    background: none;
                    padding: 0;
                    border-radius: 0;
                }
                .finding-description {
                    width: 100%;
                    margin: 0;
                    background: #fff;
                    border: none;
                    font-size: 14px;
                    font-weight: 400;
                    line-height: 1.45;
                    color: #101828;
                    font-family: Arial, sans-serif;
                    resize: vertical;
                    min-height: 78px;
                }
                .finding-description:focus {
                    outline: none;
                }
                .card-footer {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 16px;
                    padding: 14px 18px;
                    border-top: 1px solid #eaecf0;
                    background: #fff;
                }
                .link-btn {
                    background: none;
                    border: none;
                    color: #0032c8;
                    font-size: 15px;
                    font-weight: 500;
                    text-decoration: underline;
                    cursor: pointer;
                    padding: 0;
                    line-height: 1.15;
                }
                .footer-action-buttons {
                    margin-left: auto;
                    display: flex;
                    gap: 12px;
                }
                .outline-btn {
                    min-width: 88px;
                    height: 40px;
                    padding: 0 16px;
                    border-radius: 20px;
                    background: #fff;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                }
                .danger-btn {
                    color: #d92d20;
                    border: 1.5px solid #ef4444;
                }
                .success-btn {
                    color: #16a34a;
                    border: 1.5px solid #22c55e;
                }
                .panel-footer {
                    padding: 14px 20px;
                    border-top: 1px solid #eaecf0;
                    background: #fff;
                    display: flex;
                    justify-content: flex-end;
                }
                .save-all-btn {
                    background: #000eab;
                    color: #fff;
                    border: none;
                    padding: 10px 20px;
                }
                .save-all-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }
                .internal-feedback-section { margin-top: 20px; }
                .internal-feedback-section label {
                    display: block;
                    font-size: 13px;
                    color: #667085;
                    margin-bottom: 8px;
                }
                .received-feedback-section {
                    margin-bottom: 20px;
                    padding: 14px;
                    background: #FEF2F2;
                    border: 1px solid #FEE2E2;
                    border-radius: 8px;
                }
                .received-feedback-section label {
                    display: block;
                    font-size: 11px;
                    font-weight: 700;
                    color: #B91C1C;
                    text-transform: uppercase;
                    margin-bottom: 8px;
                    letter-spacing: 0.5px;
                }
                .received-feedback-content {
                    font-size: 14px;
                    color: #1F2937;
                    line-height: 1.5;
                }
                .internal-feedback-section textarea {
                    width: 100%;
                    min-height: 110px;
                    border: 1px solid #d0d5dd;
                    border-radius: 8px;
                    padding: 10px;
                    font-size: 13px;
                    resize: vertical;
                }
                .no-findings {
                    text-align: center;
                    font-size: 13px;
                    color: #667085;
                    border: 1px dashed #d0d5dd;
                    background: #fff;
                    border-radius: 10px;
                    padding: 14px;
                }

                .status-toast { position: fixed; top: 100px; left: 50%; transform: translateX(-50%); background: #101828; color: #fff; padding: 8px 16px; borderRadius: 40px; font-size: 13px; z-index: 1000; animation: fadeIn 0.3s; }
                
                .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 1000; }
                .image-properties-modal {
                    background: #fff; border-radius: 16px; width: 95%; height: 90%; max-width: 1440px;
                    box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); overflow: hidden; display: flex; flex-direction: column;
                }
                .img-props-header {
                    padding: 20px 24px; border-bottom: 1px solid #f0f0f0;
                    display: flex; justify-content: space-between; align-items: center; flex-shrink: 0;
                }
                .img-props-title { margin: 0; font-size: 20px; font-weight: 700; color: #1a1a1a; }
                .img-props-url { margin: 4px 0 0 0; font-size: 13px; color: #666; }
                .img-props-close {
                    background: #f5f5f5; border: none; font-size: 24px; cursor: pointer; color: #666;
                    width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; line-height: 1;
                }
                .img-props-close:hover { background: #eee; }
                .img-props-body { flex: 1; display: flex; overflow: hidden; min-height: 0; }
                .img-props-left {
                    width: 40%; min-width: 350px; border-right: 1px solid #f0f0f0; padding: 24px;
                    overflow-y: auto; background: #fafafa;
                }
                .img-props-section-title { font-size: 16px; font-weight: 600; color: #1a1a1a; margin-bottom: 20px; display: flex; align-items: center; gap: 8px; }
                .img-props-section-title .accent-blue { width: 4px; height: 16px; background: #000EAB; border-radius: 2px; }
                .img-props-section-title .accent-green { width: 4px; height: 16px; background: #059669; border-radius: 2px; }
                .img-props-cards { display: flex; flex-direction: column; gap: 16px; }
                .img-props-card {
                    background: #fff; padding: 16px; border-radius: 12px; border: 1px solid #f0f0f0;
                }
                .img-props-card label { display: block; font-weight: 600; color: #888; font-size: 11px; text-transform: uppercase; margin-bottom: 4px; }
                .img-props-card .value-highlight { font-size: 14px; color: #000EAB; font-weight: 600; }
                .img-props-card .hash-value { font-size: 12px; font-family: monospace; word-break: break-all; background: #f8f9fa; padding: 8px; border-radius: 6px; border: 1px dashed #ddd; }
                .img-props-card .url-ref { font-size: 12px; color: #000EAB; word-break: break-all; }
                .img-props-right { flex: 1; display: flex; flex-direction: column; position: relative; background: #fff; min-width: 0; }
                .img-props-flow-area { flex: 1; position: relative; min-height: 300px; }
                .img-props-footer {
                    padding: 16px 24px; border-top: 1px solid #f0f0f0;
                    display: flex; justify-content: flex-end; gap: 12px; flex-shrink: 0;
                }
                .img-props-btn {
                    padding: 10px 20px; border-radius: 10px; font-weight: 600; font-size: 14px; cursor: pointer;
                }
                .img-props-btn.secondary { background: #fff; color: #1a1a1a; border: 1px solid #e0e0e0; }
                .img-props-btn.secondary:hover { background: #f8f9fa; border-color: #ccc; }
                .img-props-btn.primary { background: #000EAB; color: #fff; border: none; padding: 10px 32px; }
                .img-props-btn.primary:hover { background: #000880; }
                .laws-management-modal {
                    background: #fff; border-radius: 8px; width: 80%; max-height: 80vh; overflow: auto;
                    padding: 20px; position: relative; box-shadow: 0 20px 40px rgba(0,0,0,0.25);
                }
                .laws-modal-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
                .laws-modal-title { margin: 0 0 8px 0; font-size: 20px; font-weight: 600; color: #333; }
                .laws-dark-pattern-pill {
                    font-size: 14px; font-weight: 500; color: #7B1FA2; background: #F3E5F5;
                    padding: 8px 16px; border-radius: 20px; display: inline-block;
                }
                .laws-header-actions { display: flex; gap: 12px; align-items: center; }
                .laws-icon-btn {
                    width: 40px; height: 40px; border-radius: 50%; border: none; cursor: pointer;
                    display: flex; align-items: center; justify-content: center; font-size: 18px;
                }
                .laws-icon-btn.add { background: #000EAB; color: #fff; }
                .laws-save-btn {
                    background: #000EAB; color: #fff; border: none; padding: 8px 20px;
                    border-radius: 20px; font-size: 14px; font-weight: 500; cursor: pointer;
                }
                .laws-close-btn {
                    background: transparent; border: 1px solid #000EAB; color: #000EAB;
                    width: 40px; height: 40px; min-width: 40px; border-radius: 50%; cursor: pointer; font-size: 16px;
                    display: flex; align-items: center; justify-content: center;
                }
                .laws-instructions {
                    font-size: 14px; color: #6c757d; background: #f8f9fa; padding: 12px 16px;
                    border-radius: 8px; border: 1px solid #e9ecef; margin-bottom: 20px;
                }
                .laws-filter-bar {
                    display: flex; align-items: center; gap: 16px; margin-bottom: 20px;
                    padding: 16px; background: #f8f9fa; border-radius: 8px; border: 1px solid #e9ecef;
                }
                .laws-filter-bar select {
                    padding: 8px 12px; border-radius: 6px; border: 1px solid #ced4da;
                    font-size: 14px; background: #fff; min-width: 150px;
                }
                .laws-table-wrap { overflow-x: auto; }
                .laws-management-table { width: 100%; border-collapse: separate; border-spacing: 0; }
                .laws-management-table thead tr { background: #f8f9fa; border-bottom: 2px solid #dee2e6; }
                .laws-management-table th {
                    padding: 12px 8px; text-align: left; font-size: 14px; font-weight: 600; color: #495057;
                }
                .laws-management-table tbody tr { border-bottom: 1px solid #e9ecef; }
                .laws-management-table tbody tr.alt { background: #f8f9fa; }
                .laws-management-table tbody td { padding: 12px 8px; font-size: 14px; }
                .laws-management-table tbody td.bold { font-weight: bold; color: #000; }
                .laws-action-eye {
                    background: none; border: none; cursor: pointer; padding: 4px; border-radius: 4px;
                    display: inline-flex; align-items: center; justify-content: center;
                }
                .laws-action-eye { color: #007bff; }
                .laws-action-eye:hover { background: #e7f1ff; }
                .laws-no-link { color: #6c757d; font-size: 12px; }
                .modal-container-large { background: #fff; width: 800px; max-width: 90vw; border-radius: 16px; overflow: hidden; box-shadow: 0 20px 40px rgba(0,0,0,0.2); }
                .modal-header { padding: 16px 24px; border-bottom: 1px solid #eaecf0; display: flex; justify-content: space-between; }
                .modal-content { padding: 24px; max-height: 70vh; overflow-y: auto; }
                
                .nav-info { display: flex; align-items: center; gap: 12px; }
                .nav-divider { color: #ccc; font-weight: 300; }
                .nav-journey { font-weight: 600; color: #000EAB; }
                .nav-page { font-weight: 400; color: #666; }

                .finding-card.editing { border: 2px solid #000EAB; box-shadow: 0 4px 12px rgba(0, 14, 171, 0.1); }
                .dp-type-select, .response-type-select, .priority-select, .law-correctness-select {
                    padding: 4px 8px; border-radius: 4px; border: 1px solid #ced4da;
                    font-size: 13px; background: #fff; outline: none; cursor: pointer;
                }
                .dp-type-select { min-width: 220px; max-width: 280px; }
                .dp-type-select:focus, .response-type-select:focus, .priority-select:focus, .law-correctness-select:focus { border-color: #000EAB; }

                @keyframes fadeIn { from { opacity: 0; transform: translate(-50%, -10px); } to { opacity: 1; transform: translate(-50%, 0); } }
            `}</style>
        </div>
    );
};

export default SessionViewer;
